import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";
import useApi from "../../hook/useApi";
import Loader from "../Loader/Loader";

const TrialBalanceLayer = () => {
  const { AsyncGetApiCall } = useApi();
  const [loading, setLoading] = useState(true);
  const [debitData, setDebitData] = useState([]);
  const [creditData, setCreditData] = useState([]);

  const fetchTrialBalance = useCallback(async () => {
    try {
      setLoading(true);
      const res = await AsyncGetApiCall("/users/dynamic-list", { limitVal: 1000 });
      if (res?.success && Array.isArray(res.data)) {
        const debits = [];
        const credits = [];

        res.data.forEach((acc) => {
          const balance = acc.currentBalance || 0;
          const row = {
            name: `${acc.name} (${acc.loginId})`,
            amount: Math.abs(balance),
          };

          if (balance < 0) {
            debits.push(row);
          } else if (balance > 0) {
            credits.push(row);
          }
        });

        setDebitData(debits);
        setCreditData(credits);
      }
    } catch (error) {
      console.error("Failed to fetch trial balance", error);
    } finally {
      setLoading(false);
    }
  }, [AsyncGetApiCall]);

  useEffect(() => {
    fetchTrialBalance();
  }, [fetchTrialBalance]);

  const debitTotal = debitData.reduce((sum, row) => sum + row.amount, 0);
  const creditTotal = creditData.reduce((sum, row) => sum + row.amount, 0);

  const formatAmountDisplay = (amount) => {
    const n = Number(amount) || 0;
    const isNegative = n < 0;
    const isZero = n === 0;

    const formatted = Math.abs(n).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return {
      display: `${isNegative ? "-" : ""}₹${formatted}`,
      className: isZero || !isNegative ? "text-success-600" : "text-danger-600"
    };
  };

  const handleSave = () => {
    console.log("Save trial balance");
  };

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
        <h6 className="fw-semibold mb-0">Trial Balance</h6>
        <ul className="d-flex align-items-center gap-2">
          <li className="fw-medium">
            <a
              href="/dashboard"
              className="d-flex align-items-center gap-1 hover-text-primary"
            >
              <Icon
                icon="solar:home-smile-angle-outline"
                className="icon text-lg"
              />
              Dashboard
            </a>
          </li>
          <li>-</li>
          <li className="fw-medium">Accounts</li>
          <li>-</li>
          <li className="fw-medium">Trial Balance</li>
        </ul>
      </div>

      <div className="card shadow-sm radius-16 border mb-24">
        <div className="card-header border-bottom p-24 d-flex justify-content-between align-items-center">
          <h6 className="mb-0 text-lg">Trial Balance</h6>
          <button type="button" onClick={handleSave} className="btn btn-primary radius-8 px-24 py-10">
            Save
          </button>
        </div>

        <div className="card-body p-24">
          {loading && debitData.length === 0 && creditData.length === 0 ? (
            <div className="d-flex justify-content-center my-5 py-5">
              <Loader size={40} />
            </div>
          ) : (
            <div style={{ opacity: loading ? 0.5 : 1, transition: "opacity 0.2s" }} className="d-flex flex-column gap-4">
              <div className="row gx-4 gy-4 mb-4">
                {/* Debit Column */}
                <div className="col-12 col-lg-6">
                  <div className="card border h-100 radius-12 overflow-hidden shadow-none">
                    <div className="bg-neutral-100 px-16 py-12 d-flex justify-content-between align-items-center border-bottom">
                      <span className="fw-bold text-primary-light text-sm text-uppercase">Name</span>
                      <span className="fw-bold text-primary-light text-sm text-uppercase">Debit</span>
                    </div>
                    <div className="card-body p-16 d-flex flex-column h-100">
                      <div className="flex-grow-1">
                        {debitData.length === 0 ? (
                          <div className="text-center py-4 text-secondary-light fw-bold">
                            No Debit Records
                          </div>
                        ) : (
                          <ul className="list-unstyled mb-0 d-flex flex-column gap-3">
                            {debitData.map((row, i) => (
                              <li
                                key={i}
                                className="d-flex justify-content-between align-items-center pb-3 border-bottom"
                              >
                                <span className="text-sm text-secondary fw-semibold">
                                  {row.name}
                                </span>
                                <span className={`text-sm fw-bold ${formatAmountDisplay(-row.amount).className}`}>
                                  {formatAmountDisplay(-row.amount).display}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                    <div className="bg-primary-50 px-16 py-12 d-flex justify-content-between align-items-center border-top mt-auto">
                      <span className="fw-bolder text-primary-light text-sm text-uppercase">
                        Total:
                      </span>
                      <span className={`fw-bolder text-sm ${formatAmountDisplay(-debitTotal).className}`}>
                        {formatAmountDisplay(-debitTotal).display}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Credit Column */}
                <div className="col-12 col-lg-6">
                  <div className="card border h-100 radius-12 overflow-hidden shadow-none">
                    <div className="bg-neutral-100 px-16 py-12 d-flex justify-content-between align-items-center border-bottom">
                      <span className="fw-bold text-primary-light text-sm text-uppercase">Name</span>
                      <span className="fw-bold text-primary-light text-sm text-uppercase">Credit</span>
                    </div>
                    <div className="card-body p-16 d-flex flex-column h-100">
                      <div className="flex-grow-1">
                        {creditData.length === 0 ? (
                          <div className="text-center py-4 text-secondary-light fw-bold text-uppercase">
                            No Credit Records
                          </div>
                        ) : (
                          <ul className="list-unstyled mb-0 d-flex flex-column gap-3">
                            {creditData.map((row, i) => (
                              <li
                                key={i}
                                className="d-flex justify-content-between align-items-center pb-3 border-bottom"
                              >
                                <span className="text-sm text-secondary fw-semibold">
                                  {row.name}
                                </span>
                                <span className={`text-sm fw-bold ${formatAmountDisplay(row.amount).className}`}>
                                  {formatAmountDisplay(row.amount).display}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                    <div className="bg-primary-50 px-16 py-12 d-flex justify-content-between align-items-center border-top mt-auto">
                      <span className="fw-bolder text-primary-light text-sm text-uppercase">
                        Total:
                      </span>
                      <span className={`fw-bolder text-sm ${formatAmountDisplay(creditTotal).className}`}>
                        {formatAmountDisplay(creditTotal).display}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Final Difference Box */}
              <div className="bg-primary-50 radius-12 p-3 d-flex align-items-center justify-content-center border">
                <h5 className="text-primary-light fw-bolder mb-0 text-uppercase text-2xl">
                  Total Difference: {formatAmountDisplay(creditTotal - debitTotal).display}
                </h5>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TrialBalanceLayer;
